<script lang="ts">
	import { onMount } from 'svelte';
	import { browser } from '$app/env';
	import { page } from '$app/state';
	import { asset } from '$app/paths';
	import { fade } from 'svelte/transition';

	import TopAppBar, { Row, Section, Title, AutoAdjust } from '@smui/top-app-bar';
	import IconButton from '@smui/icon-button';
	import Button, { Label } from '@smui/button';
	import Drawer, { AppContent, Content, Header, Subtitle, Scrim } from '@smui/drawer';
	import List, { Item, Text } from '@smui/list';
	import { mdiWeatherSunny, mdiWeatherNight, mdiLogout, mdiMenu, mdiShareVariant } from '@mdi/js';

	import SvgIcon from '#lib/SvgIcon.svelte';
	import auth from '#stores/authStore';
	import type { User, UserInfo } from 'firebase/auth';
	import userStore from '#stores/userStore';
	import { dateStore } from '#stores/dateStore';

	let { children } = $props();

	let topAppBar: TopAppBar | undefined = $state();
	let mobileDrawerOpen = $state(false);
	let WrapperComponent: any = $state(Content);
	let inApp = $derived(page.url.pathname.startsWith('/app'));
	let isLoginOrSignup = $derived(
		page.url.pathname.startsWith('/login') || page.url.pathname.startsWith('/signup')
	);
	let onInvitePage = $derived(page.url.pathname.startsWith('/app/invite'));

	$effect(() => {
		if (inApp && !$auth.waitingForFirebase && !$auth.isLoggedIn) {
			console.warn(`Unauthenticated access to /app not allowed, redirecting to login`);
			if (browser) {
				window.location.href = `/`;
			}
		}
		/* AutoAdjust causes flickering of the hero content on the index page, so only use it inside the app */
		if (inApp || isLoginOrSignup) {
			WrapperComponent = AutoAdjust;
		}
	});
	let authIsReady: User | null | undefined = $state();

	let swipingLeft = $state(false);
	let swipingRight = $state(false);
	let touchStartX = $state(0);
	let touchStartY = $state(0);
	let touchEndX = $state(0);
	let touchEndY = $state(0);
	function checkDirection() {
		if (touchEndX < touchStartX - 50 && Math.abs(touchEndY - touchStartY) < 25) {
			dateStore.adjustDate(7);
		}
		if (touchEndX > touchStartX + 50 && Math.abs(touchEndY - touchStartY) < 25) {
			dateStore.adjustDate(-7);
		}
	}

	function drawerNavigateTo(path: string) {
		mobileDrawerOpen = false;
		window.location.href = path;
	}

	function drawerLogout() {
		mobileDrawerOpen = false;
		auth.logout();
	}

	onMount(async () => {
		authIsReady = await auth.waitForFirebase();
		document.addEventListener('touchstart', (e) => {
			touchStartX = e.changedTouches[0].screenX;
			touchStartY = e.changedTouches[0].screenY;
		});
		document.addEventListener('touchend', (e) => {
			touchEndX = e.changedTouches[0].screenX;
			touchEndY = e.changedTouches[0].screenY;
			swipingLeft = false;
			swipingRight = false;
			checkDirection();
		});
		document.addEventListener('touchmove', (e) => {
			let currentTouch = e.touches[0];
			if (touchStartX && touchStartY) {
				if (touchStartX < currentTouch.screenX - 25) {
					if (Math.abs(touchStartY - currentTouch.screenY) < 25) {
						swipingLeft = true;
					}
				} else if (touchStartX > currentTouch.screenX + 25) {
					if (Math.abs(touchStartY - currentTouch.screenY) < 25) {
						swipingRight = true;
					}
				}
			}
		});
	});
</script>

<TopAppBar bind:this={topAppBar} variant="fixed" aria-label="Top Navigation">
	<Row>
		<Section align="start" toolbar class="mobile">
			<IconButton
				aria-label="Menu"
				title="Menu"
				onclick={() => (mobileDrawerOpen = !mobileDrawerOpen)}
				><SvgIcon mdiIcon={mdiMenu} /></IconButton>
		</Section>
		<Section>
			<Title>
				<div
					class="clickable-title"
					onclick={() => (window.location.href = '/')}
					onkeydown={(e) => e?.key === 'Enter' && (window.location.href = '/')}
					role="button"
					tabindex="0"
					title="Go to the Landing page">
					<img src={asset('images/logo.png')} alt="Logo" />
					Simple Weekly Meal Planner
				</div>
			</Title>
		</Section>

		<!-- Desktop Navigation -->
		{#if authIsReady !== undefined}
			<Section align="end" toolbar class="desktop" aria-label="Navigation Buttons">
				{#if $auth.isLoggedIn}
					{#if !inApp || onInvitePage}
						<Button onclick={() => (window.location.href = '/app')}
							><Label>Go to App</Label></Button>
					{:else}
						{$auth.user?.displayName || $auth.user?.email}
						<IconButton aria-label="Logout" title="Logout" onclick={auth.logout}>
							<SvgIcon mdiIcon={mdiLogout} />
						</IconButton>
					{/if}
				{:else}
					<Button onclick={() => (window.location.href = '/login')}><Label>Login</Label></Button>
					<Button onclick={() => (window.location.href = '/signup')}><Label>Sign Up</Label></Button>
				{/if}
			</Section>
		{/if}
	</Row>
</TopAppBar>
<WrapperComponent {topAppBar}>
	<!-- Mobile Navigation -->
	<Drawer variant="modal" fixed={true} bind:open={mobileDrawerOpen} class="mobile-drawer">
		<Content>
			<List>
				<Header>
					<Title class="drawer-header-title">Menu</Title>
				</Header>
				{#if $auth.isLoggedIn}
					{#if !inApp || onInvitePage}
						<Item onclick={() => drawerNavigateTo('/app')}><Text>Go to App</Text></Item>
					{/if}
					<!-- <Item onclick={darkMode.toggleDarkMode}
						><Text>{$darkMode ? 'Disable Dark Mode' : 'Enable Dark Mode'}</Text></Item> -->
					{#if !onInvitePage}
						<Item onclick={() => drawerNavigateTo('/app/invite')}>
							<Text>Invite Collaborators</Text>
						</Item>
					{/if}
					<Item onclick={drawerLogout}><Text>Logout</Text></Item>
				{:else}
					<Item onclick={() => drawerNavigateTo('/login')}><Text>Login</Text></Item>
					<Item onclick={() => drawerNavigateTo('/signup')}><Text>Sign Up</Text></Item>
				{/if}
				<Item
					onclick={() =>
						(window.location.href =
							'https://play.google.com/store/apps/details?id=com.fonner.simpleweeklymealplanner')}
					><Text>Version {import.meta.env.VITE_APPVERSION}</Text></Item>
			</List>
		</Content>
	</Drawer>
	<Scrim fixed={true} />

	<AppContent class="app-content">
		{#if swipingLeft}
			<div class="swiping-left" transition:fade|global={{ duration: 150 }}></div>
		{/if}
		{#if swipingRight}
			<div class="swiping-right" transition:fade|global={{ duration: 150 }}></div>
		{/if}
		<div id="main" class="main-content" class:swipingLeft class:swipingRight>
			{@render children?.()}
		</div>
	</AppContent>
</WrapperComponent>

<style>
	:global(.button:focus) {
		text-decoration: underline;
		box-shadow: 5px 5px 25px 0px rgba(0, 0, 0, 0.25);
	}
	.clickable-title {
		cursor: pointer;
		display: flex;
		font-size: 1rem;
	}
	.clickable-title img {
		width: 30px;
		height: 30px;
	}
	.clickable-title:focus {
		border: 1px solid grey;
	}
	.swiping-left {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 20px;
		background: linear-gradient(to right, grey, transparent);
	}
	.swiping-right {
		position: absolute;
		top: 0;
		right: 0;
		height: 100%;
		width: 20px;
		background: linear-gradient(to left, grey, transparent);
	}

	@media (min-width: 992px) {
		.clickable-title {
			font-size: 1.5rem;
		}
		.clickable-title img {
			width: 36px;
			height: 36px;
		}
	}
	:global(.drawer-header-title) {
		padding: 0;
	}
	:global(.mdc-drawer__header) {
		min-height: unset;
	}
	:global(.mini.mdc-button--raised) {
		font-size: 0.7rem;
		padding: 0 1em;
	}
	@media print {
		:global(.mobile) {
			display: none;
		}
		:global(.mdc-top-app-bar--fixed) {
			position: unset;
		}
		:global(.mdc-top-app-bar--fixed-adjust) {
			padding-top: 0;
		}
	}
</style>
